type SpeedValue = 3000 | 2000 | 1000;

interface AnimationSpeedControlProps {
  speed: SpeedValue;
  onSpeedChange: (speed: SpeedValue) => void;
}

const speedMap: SpeedValue[] = [3000, 2000, 1000];
const speedLabels = ['3s', '2s', '1s'];

const sliderFromSpeed = (speed: SpeedValue): number => speedMap.indexOf(speed);
const speedFromSlider = (value: number): SpeedValue => speedMap[value] ?? 2000;

export function AnimationSpeedControl({ speed, onSpeedChange }: AnimationSpeedControlProps) {
  const sliderValue = sliderFromSpeed(speed);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onSpeedChange(speedFromSlider(Number(e.target.value)));
  };

  // Calculate the fill percentage for the active range coloring
  const fillPercent = (sliderValue / 2) * 100;

  return (
    <div className="animation-speed-panel border border-line rounded-lg px-2 py-1">
      <label
        htmlFor="animation-speed-slider"
        className="font-semibold text-ink mb-0.5 block"
        style={{ fontSize: 'clamp(0.75rem, 1.3vw, 0.9rem)' }}
      >
        Animation Speed
      </label>

      <div className="relative">
        <input
          id="animation-speed-slider"
          type="range"
          min={0}
          max={2}
          step={1}
          value={sliderValue}
          onChange={handleChange}
          aria-label={`Animation speed: ${speedLabels[sliderValue]} per card`}
          className="speed-slider w-full"
          style={{
            background: `linear-gradient(to right, var(--primary-600) ${fillPercent}%, #d8ceae ${fillPercent}%)`,
          }}
        />
      </div>

      <div className="flex justify-between mt-0.5">
        {speedLabels.map((label) => (
          <span
            key={label}
            className="text-ink-soft"
            style={{ fontSize: 'clamp(0.75rem, 1.3vw, 0.9rem)' }}
          >
            {label}
          </span>
        ))}
      </div>

      <style>{`
        .speed-slider {
          -webkit-appearance: none;
          appearance: none;
          height: 0.375rem;
          border-radius: 9999px;
          cursor: pointer;
        }
        .speed-slider:focus {
          outline: none;
        }
        .speed-slider:focus-visible {
          outline: 2px solid var(--primary-700);
          outline-offset: 2px;
        }
        .speed-slider::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          width: 1.5rem;
          height: 1.5rem;
          border-radius: 50%;
          background: var(--primary-600);
          cursor: pointer;
          border: none;
        }
        .speed-slider::-moz-range-thumb {
          width: 1.5rem;
          height: 1.5rem;
          border-radius: 50%;
          background: var(--primary-600);
          cursor: pointer;
          border: none;
        }
        .speed-slider::-moz-range-track {
          height: 0.375rem;
          border-radius: 9999px;
          background: transparent;
        }
      `}</style>
    </div>
  );
}
